import {
  selectToeKickSuperSkuId,
  selectProductsModalType
} from '../selectors';
import { updateCartWithToekicks } from '../../../utils/toekickCalculator';
import logNewRelicCustom from '../../../utils/logNewRelicCustom';
import { NEW_RELIC_EVENTS } from '../../../constants';

/**
 * Zustand action to recalculate toekicks for one or more designs
 * @param {Function} set - Zustand set function
 * @param {Function} get - Zustand get function
 * @param {string} host - API host URL
 * @param {Array|Object} designs - Single design or array of designs to recalculate toekicks for
 * @returns {Promise<Array>} Array of designs with recalculated toekicks
 */
export default async (set, get, host, designs) => {
  try {
    const stateCabinetVisualizer = get().cabinetVisualizer;
    const { toeKickMetadata, choices } = stateCabinetVisualizer;

    // Validate inputs
    if (!designs) {
      return [];
    }

    // Ensure designs is an array
    const designsArray = Array.isArray(designs) ? designs : [designs];

    if (designsArray.length === 0) {
      return [];
    }

    if (!toeKickMetadata) {
      return designsArray;
    }

    // Recalculate toekicks for each design
    const updatedDesigns = await Promise.all(
      designsArray.map(async (design) => {
        try {
          // Ensure all products have ProductParentIds populated
          // Template products have ChoiceId which should map to ProductParentIds
          const ensuredProductParentIds = { ...design.ProductParentIds };
          if (design.Products) {
            design.Products.forEach((product) => {
              if (product.Sku > 0 && product.ChoiceId && !ensuredProductParentIds[product.Sku]) {
                ensuredProductParentIds[product.Sku] = product.ChoiceId;
              }
            });
          }

          // Create getProductType function scoped to this design
          const getProductType = (sku) => selectProductsModalType(
            { ...stateCabinetVisualizer,
              designs: { ...stateCabinetVisualizer.designs,
                [design.Id]: { ...design, ProductParentIds: ensuredProductParentIds } } },
            sku,
            design.Id
          );

          // Get toekick SuperSKU for this specific design's style
          const designStyleChoiceId = design.DesignStyleChoiceid;
          const toeKickSuperSkuId = Number(
            selectToeKickSuperSkuId({ choices, styleChoiceId: designStyleChoiceId })
          );

          if (!toeKickSuperSkuId) {
            return design;
          }

          // Update cart with toekicks for this design using ensured ProductParentIds
          const result = await updateCartWithToekicks({
            products: design.Products || [],
            cart: design.Cart || [],
            productParentIds: ensuredProductParentIds,
            toeKickSuperSkuId,
            toeKickMetadata,
            addedProductInfo: {}, // No specific product being added - recalculate all
            getProductType,
            host,
            collectionId: design.CollectionId
          });

          // Return design with updated cart and ProductParentIds
          return {
            ...design,
            Cart: result.cart,
            ProductParentIds: {
              ...design.ProductParentIds,
              ...result.productParentIds
            }
          };
        } catch (error) {
          logNewRelicCustom(NEW_RELIC_EVENTS.RECALCULATE_TOEKICKS_FAILED, {
            designId: design.Id,
            error: error.message
          });
          // Return original design on error
          return design;
        }
      })
    );

    logNewRelicCustom(NEW_RELIC_EVENTS.RECALCULATE_TOEKICKS_SUCCESS, {
      designsCount: updatedDesigns.length
    });

    return updatedDesigns;
  } catch (error) {
    logNewRelicCustom(NEW_RELIC_EVENTS.RECALCULATE_TOEKICKS_FAILED, {
      error: error.message
    });
    // Return original designs on error
    return Array.isArray(designs) ? designs : [designs];
  }
};
